import { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, BookCheck, BookPlus, BookX, Search, ArrowRight, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AsyncCombobox } from '@/components/ui/async-combobox';
import { Button } from '@/components/ui/button';
import { matchCourses, searchCoursesAPI } from '../utils/matchCourses';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

export function QualificationsReviewStep({ parsedQualifications, projectId, qualMatches, setQualMatches }) {
  const { company } = useAuth();
  const [loading, setLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [expandedIndex, setExpandedIndex] = useState(null);

  useEffect(() => {
    if (parsedQualifications.length > 0 && qualMatches.length === 0) {
      runMatching();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedQualifications]);

  const runMatching = useCallback(async () => {
    if (!company?.id || !projectId || parsedQualifications.length === 0) return;
    setLoading(true);
    setProgress({ done: 0, total: parsedQualifications.length });

    try {
      const { data: existing } = await supabase
        .from('compliance_requirements')
        .select('id, name, course_link_id, course_link_type')
        .eq('project_id', projectId);

      const results = await matchCourses(
        parsedQualifications,
        existing || [],
        company.id,
        (done, total) => setProgress({ done, total })
      );

      // Auto-select the first search result for API matches
      const enriched = results.map(r => {
        if (r.matchType === 'api_match' && r.searchResults.length > 0 && !r.selectedCourse) {
          return { ...r, selectedCourse: r.searchResults[0] };
        }
        return r;
      });

      setQualMatches(enriched);
    } catch (err) {
      console.error('Error matching courses:', err);
    } finally {
      setLoading(false);
    }
  }, [company?.id, projectId, parsedQualifications, setQualMatches]);

  const updateMatch = useCallback((index, updates) => {
    setQualMatches(prev => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  }, [setQualMatches]);

  const handleCourseSearch = useCallback(async (searchTerm) => {
    if (!company?.id) return [];
    return searchCoursesAPI(searchTerm, company.id);
  }, [company?.id]);

  if (loading) {
    const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="mt-3 text-sm text-muted-foreground">
          Matching qualifications... {progress.total > 0 && `${pct}%`}
        </p>
        {progress.total > 0 && (
          <div className="w-48 mt-3 bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
      </div>
    );
  }

  const existingCount = qualMatches.filter(q => q.action === 'use_existing').length;
  const creatingCount = qualMatches.filter(q => q.action === 'create_from_course' || q.action === 'create_custom').length;
  const skippedCount = qualMatches.filter(q => q.action === 'skip').length;

  const filtered = searchFilter
    ? qualMatches.filter(q => q.rawName.toLowerCase().includes(searchFilter.toLowerCase()))
    : qualMatches;

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <Badge variant="outline" className="gap-1.5 py-1">
          <BookCheck className="h-3.5 w-3.5 text-green-600" />
          {existingCount} existing
        </Badge>
        <Badge variant="outline" className="gap-1.5 py-1">
          <BookPlus className="h-3.5 w-3.5 text-blue-600" />
          {creatingCount} new
        </Badge>
        <Badge variant="outline" className="gap-1.5 py-1">
          <BookX className="h-3.5 w-3.5 text-gray-400" />
          {skippedCount} skipped
        </Badge>

        <div className="ml-auto relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input
            placeholder="Filter qualifications..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="h-8 pl-8 w-56 text-sm"
          />
        </div>
      </div>

      {/* Qualifications list */}
      <ScrollArea className="h-[440px] border rounded-lg">
        <div className="divide-y">
          {filtered.map((qual) => {
            const realIndex = qualMatches.indexOf(qual);
            const isExpanded = expandedIndex === realIndex;
            const matchedName = qual.action === 'use_existing'
              ? qual.matchedRequirement?.name
              : qual.selectedCourse?.label || null;

            return (
              <div key={realIndex} className="hover:bg-gray-50/50">
                {/* Main row */}
                <div className="flex items-start gap-3 px-4 py-3">
                  {/* Left: spreadsheet name + match info */}
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="text-sm font-medium truncate">{qual.rawName}</p>
                      <MatchBadge matchType={qual.matchType} />
                    </div>

                    {qual.action !== 'skip' && (
                      <div className="flex items-center gap-2 text-xs min-w-0">
                        <ArrowRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                        {qual.action === 'use_existing' && qual.matchedRequirement && (
                          <span className="text-green-700 truncate">
                            Uses existing: <span className="font-medium">{qual.matchedRequirement.name}</span>
                          </span>
                        )}
                        {qual.action === 'create_from_course' && matchedName && (
                          <span className="text-blue-700 truncate">
                            Create from: <span className="font-medium">{matchedName}</span>
                          </span>
                        )}
                        {qual.action === 'create_from_course' && !matchedName && (
                          <span className="text-muted-foreground italic">No course selected</span>
                        )}
                        {qual.action === 'create_custom' && (
                          <span className="text-muted-foreground truncate">
                            Create as custom: <span className="font-medium">{qual.rawName}</span>
                          </span>
                        )}
                      </div>
                    )}
                    {qual.action === 'skip' && (
                      <p className="text-xs text-muted-foreground italic flex items-center gap-2">
                        <ArrowRight className="h-3 w-3 flex-shrink-0" />
                        Will be skipped
                      </p>
                    )}
                  </div>

                  {/* Right: action + expand */}
                  <div className="flex items-center gap-1.5 flex-shrink-0 pt-0.5">
                    <Select
                      value={qual.action}
                      onValueChange={(val) => updateMatch(realIndex, { action: val })}
                    >
                      <SelectTrigger className="w-36 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {qual.matchedRequirement && (
                          <SelectItem value="use_existing">Use Existing</SelectItem>
                        )}
                        <SelectItem value="create_from_course">Create (Course)</SelectItem>
                        <SelectItem value="create_custom">Create Custom</SelectItem>
                        <SelectItem value="skip">Skip</SelectItem>
                      </SelectContent>
                    </Select>

                    {qual.action !== 'skip' && qual.action !== 'use_existing' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 flex-shrink-0"
                        onClick={() => setExpandedIndex(isExpanded ? null : realIndex)}
                      >
                        {isExpanded
                          ? <ChevronUp className="h-4 w-4" />
                          : <ChevronDown className="h-4 w-4" />
                        }
                      </Button>
                    )}
                  </div>
                </div>

                {/* Expanded panel */}
                {isExpanded && (qual.action === 'create_from_course' || qual.action === 'create_custom') && (
                  <ExpandedCoursePanel
                    qual={qual}
                    realIndex={realIndex}
                    updateMatch={updateMatch}
                    handleCourseSearch={handleCourseSearch}
                  />
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center py-8 text-sm text-muted-foreground">
              {searchFilter ? 'No qualifications match your filter' : 'No qualifications found'}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

function ExpandedCoursePanel({ qual, realIndex, updateMatch, handleCourseSearch }) {
  const results = qual.searchResults || [];

  return (
    <div className="px-4 pb-3">
      <div className="ml-5 border-l-2 border-blue-200 pl-3 space-y-2">
        {/* Course search combobox */}
        <div>
          <p className="text-xs text-muted-foreground mb-1">Search for a course:</p>
          <AsyncCombobox
            placeholder="Type to search courses..."
            onSearch={handleCourseSearch}
            value={qual.selectedCourse?.value || ''}
            displayLabel={qual.selectedCourse?.label || ''}
            onValueChange={(value) => {
              if (!value) {
                updateMatch(realIndex, {
                  selectedCourse: null,
                  action: 'create_custom'
                });
              }
            }}
            onSelectionChange={(courseData) => {
              const courseName = courseData?.name || courseData?.title;
              if (courseName) {
                updateMatch(realIndex, {
                  selectedCourse: { value: courseName, label: courseName, data: courseData },
                  action: 'create_from_course'
                });
              }
            }}
            className="h-8 text-xs"
          />
        </div>

        {/* Suggestions list */}
        {results.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-1">
              {results.length} suggestion{results.length !== 1 ? 's' : ''}:
            </p>
            <div className="border rounded max-h-[160px] overflow-y-auto">
              {results.map((result, i) => {
                const isSelected = qual.selectedCourse?.value === result.value;
                return (
                  <button
                    key={i}
                    onClick={() => updateMatch(realIndex, {
                      selectedCourse: result,
                      action: 'create_from_course'
                    })}
                    className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 border-b last:border-b-0 transition-colors ${
                      isSelected
                        ? 'bg-blue-50 text-blue-900'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div className={`w-3.5 h-3.5 rounded-full border flex-shrink-0 flex items-center justify-center ${
                      isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                    }`}>
                      {isSelected && <Check className="h-2 w-2 text-white" />}
                    </div>
                    <span className="truncate">{result.label}</span>
                    {result.group === 'Custom Courses' && (
                      <span className="text-[10px] text-muted-foreground ml-auto flex-shrink-0">custom</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MatchBadge({ matchType }) {
  switch (matchType) {
    case 'existing_exact':
      return <Badge className="bg-green-100 text-green-800 text-[10px] py-0 flex-shrink-0">Exact match</Badge>;
    case 'existing_fuzzy':
      return <Badge className="bg-yellow-100 text-yellow-800 text-[10px] py-0 flex-shrink-0">Fuzzy match</Badge>;
    case 'api_match':
      return <Badge className="bg-blue-100 text-blue-800 text-[10px] py-0 flex-shrink-0">Course found</Badge>;
    case 'none':
      return <Badge variant="secondary" className="text-[10px] py-0 flex-shrink-0">No match</Badge>;
    default:
      return null;
  }
}
